/**
 * One filtered list of items, a page at a time.
 *
 * The brief is explicit that there is never an unbounded flat list, so this
 * hook only ever holds what has actually been asked for: a first page, and
 * whatever "Show more" has added to it. The cursor comes from the worker and
 * means "after this exact item", so items saved while somebody is reading do
 * not shuffle the page under them -- an offset would show the same thing twice.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ItemQuery, ItemSummary } from "@iltocat/shared";
import { api } from "./api.js";

export interface ItemList {
  items: ItemSummary[];
  /** How many match the filter, not how many are loaded. This is what a heading says. */
  total: number;
  loading: boolean;
  /** True while a "Show more" is in flight, so the button can say so. */
  loadingMore: boolean;
  error: string | null;
  more: boolean;
  showMore: () => void;
  /**
   * Reads every page still to come, for "select all": what is selected should
   * be what is on screen. Resolves to the whole list, or null if the filter
   * changed or a page failed on the way.
   */
  loadAll: () => Promise<ItemSummary[] | null>;
  reload: () => void;
}

export function useItems(query: ItemQuery): ItemList {
  const [items, setItems] = useState<ItemSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by every first-page load. A page that lands for an older one is
  // dropped: with the list asking for more as it scrolls, a page for the last
  // filter can otherwise arrive after the first page of the new one and be
  // appended to it.
  const generation = useRef(0);

  // The filter is an object, so it is new on every render; its *contents* are
  // what should trigger a reload, and this is the smallest honest way to say
  // that to the dependency array.
  const key = JSON.stringify(query);

  const load = useCallback(
    async (after: string | null) => {
      if (after) setLoadingMore(true);
      else {
        generation.current += 1;
        setLoading(true);
      }
      const mine = generation.current;
      setError(null);

      try {
        const asked: ItemQuery = { ...(JSON.parse(key) as ItemQuery), ...(after ? { cursor: after } : {}) };
        const page = await api.items.list(asked);
        if (mine !== generation.current) return;
        setItems((current) => (after ? [...current, ...page.items] : page.items));
        setTotal(page.total);
        setCursor(page.nextCursor);
      } catch (problem) {
        if (mine !== generation.current) return;
        setError(problem instanceof Error ? problem.message : "Could not load your items.");
      } finally {
        if (mine === generation.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [key],
  );

  const loadAll = useCallback(async (): Promise<ItemSummary[] | null> => {
    const mine = generation.current;
    let all = items;
    let after = cursor;
    if (!after) return all;
    setLoadingMore(true);
    try {
      while (after) {
        const page = await api.items.list({ ...(JSON.parse(key) as ItemQuery), cursor: after });
        if (mine !== generation.current) return null;
        all = [...all, ...page.items];
        after = page.nextCursor;
      }
      setItems(all);
      setCursor(null);
      return all;
    } catch (problem) {
      if (mine === generation.current)
        setError(problem instanceof Error ? problem.message : "Could not load the rest of your items.");
      return null;
    } finally {
      if (mine === generation.current) setLoadingMore(false);
    }
  }, [items, cursor, key]);

  useEffect(() => {
    void load(null);
  }, [load]);

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    more: cursor !== null,
    showMore: () => {
      if (cursor && !loadingMore) void load(cursor);
    },
    loadAll,
    reload: () => void load(null),
  };
}
